import { useCallback, useEffect, useRef, useState } from 'react';

import { useEvent } from 'react-use';

import { useChartContext } from '@/components/Chart/context/ChartContext';
import type { ExtendedChart, ExtendedSeriesOptionsType } from '@/components/Chart/types/highcharts';

import type { HighchartsReactRefObject } from '@highcharts/react/Stock';

const useRegularChart = ({
  chartSeries
}: {
  chartSeries: ExtendedSeriesOptionsType[] | undefined;
}) => {
  const [chart, setChart] = useState<ExtendedChart | null>(null);

  const timeoutRef = useRef<NodeJS.Timeout>(null);
  const chartContainerRef = useRef<HTMLDivElement>(null);

  const chartComponentRef = useRef<HighchartsReactRefObject | null>(null);

  const { setChartSeries, chartRef } = useChartContext();

  const handleChartRef = useCallback(
    (ref: HighchartsReactRefObject | null) => {
      if (!ref) return;

      chartComponentRef.current = ref;

      const chartInstance = ref.chart as ExtendedChart;
      setChart(chartInstance);
      chartRef.current = ref;
    },
    [chartRef]
  );

  const handleResize = useCallback(() => {
    const chart = chartComponentRef.current?.chart;
    const container = chartContainerRef.current;

    if (!chart || !container) return;

    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      chart.container.style.display = 'none';
      chart.setSize(container.getBoundingClientRect().width);
      chart.container.style.display = 'block';
    }, 100);
  }, []);

  useEvent('resize', handleResize, window);

  useEffect(() => {
    setChartSeries(chartSeries);
  }, [chartSeries, setChartSeries]);

  return { chart, handleChartRef, chartContainerRef, chartComponentRef };
};

export default useRegularChart;
